import React, { useState, useRef, useEffect } from 'react';
import { getImageUrl } from '../api';
import { useCardImages } from '../utils/imageQueue';
import { trackProductAddToCart, trackProductDetail } from '../utils/metrika';
import { takeDeepLink, useItemUrl } from '../utils/deepLink';
import { firstPlannedAvailable } from '../utils/bouquetAvailability';
import { isProductOrderable } from '../utils/unavailable';
import PhotoLightbox from './PhotoLightbox';

// compact — карточка в ленте «Есть сегодня» (блок «Сейчас нет в продаже»): без стрелок и точек
// на фото, движение пальцем вбок листает ленту, а не фото; нажатие открывает товар
function ProductCard({ product, addToCart, cartItem, updateQuantity, maxDisplayQuantity = null, cartStemUsage = 0, compact = false }) {
  const quantityInCart = cartItem?.quantity || 0;
  const isReservedInCart = !!cartItem?.reserved_by;
  const rawAvailable = product.quantity ?? 0;
  const step = product.step || 1;

  // Эффективный retail-сток: вычитаем «занятость» корзиной (включая компоненты букетов
  // в корзине), кроме того, что уже добавлено этой же карточкой стеблей.
  // usedByOthers = всего занято корзиной − моя текущая порция стеблей.
  const usedByOthers = Math.max(0, cartStemUsage - quantityInCart);
  const effectiveRetailRaw = Math.max(0, rawAvailable - usedByOthers);
  // Округление вниз до кратного step — иначе «осталось 8 при шаге 5» = доступно 5, не 8.
  const effectiveRetail = Math.floor(effectiveRetailRaw / step) * step;

  // Та же страховка для плановых поставок: букетные компоненты в корзине вычитаются
  // и из плановой (мы не знаем какую дату выберет клиент в чекауте — букет может
  // взять компоненты как из текущей, так и из плановой полки).
  const adjustedFutureStock = (product.future_stock || []).map(fs => ({
    ...fs,
    available: Math.max(0, (fs.available || 0) - usedByOthers),
  }));
  // Правило «лимит из текущего ИЛИ первой плановой по дате»: поздние большие плановые
  // в лимит НЕ входят (даже если их фактически больше).
  const futureAvailable = firstPlannedAvailable(adjustedFutureStock);

  // totalAvailable: лимит counter +/-. Берём максимум retail vs первая плановая.
  const rawTotal = Math.max(effectiveRetail, futureAvailable);
  const totalAvailable = maxDisplayQuantity ? Math.min(rawTotal, maxDisplayQuantity) : rawTotal;
  // Если этот item уже зарезервирован букетом — каталожная + кнопка блокируется,
  // юзер должен сначала убрать доминантный букет.
  const canAddMore = !isReservedInCart && quantityInCart + step <= totalAvailable;

  // Предупреждение: букет(ы) в корзине заняли весь доступный лимит цветка для заказа
  // стеблей (и retail, и плановые) — клиент уже не может добавить ни одного шага.
  // Если плановая поставка ≥ step остаётся свободной — стебли можно купить на её дату,
  // предупреждение не нужно.
  const usedByBouquetsTakesStock = usedByOthers > 0 && totalAvailable < step;

  // Ближайшая плановая поставка ПО ДАТЕ открытия с достаточным количеством (>= step).
  const nearestFuture = adjustedFutureStock
    .slice()
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))
    .find(fs => Math.floor((fs.available || 0) / step) * step > 0) || null;

  const [currentImage, setCurrentImage] = useState(0);

  // Получаем массив изображений с responsive sizes
  const hasRealImages = product.images && product.images.length > 0 && product.images[0]?.image_url;
  const images = hasRealImages 
    ? product.images.map(img => ({
        small: img.image_small || img.thumb_url || img.image_url,
        medium: img.image_medium || img.image_url,
        large: img.image_large || img.image_url,
        default: img.image_medium || img.image_url
      }))
    : [{ default: product.emoji || '💐' }];
  const totalImages = images.length || 1;
  const flipPhotos = !compact && totalImages > 1;
  // Фото в карточке — средний размер через очередь (utils/imageQueue.js), большое — в зуме
  const imageBoxRef = useRef(null);
  const cardUrls = hasRealImages ? images.map(img => getImageUrl(img.medium)) : [];
  const imageReady = useCardImages(cardUrls, imageBoxRef, currentImage);

  // Цена: для товаров с шагом > 1 показываем цену за шаг
  const displayPrice = product.price * step;
  const priceInCart = product.price * quantityInCart;

  const nextImage = () => {
    setCurrentImage((prev) => (prev + 1) % totalImages);
  };

  const prevImage = () => {
    setCurrentImage((prev) => (prev - 1 + totalImages) % totalImages);
  };

  // Свайп в сторону — листает фото на карточке; тап — открывает полноэкранный
  // просмотр (PhotoLightbox). Вертикальное движение отдаём скроллу страницы.
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const touchStartRef = useRef(null);
  const cardRef = useRef(null);

  // Пришли по ссылке на этот товар (/product/… или p_<id> из бота) — сразу открываем
  // карточку; без фото окна нет, тогда просто подводим каталог к товару. Купить нечего ни
  // сегодня, ни с поставки — ссылку не берём: каталог покажет окно «Сейчас нет в продаже»
  useEffect(() => {
    if (!isProductOrderable(product)) return;
    if (!takeDeepLink('product', product.id)) return;
    try { cardRef.current?.scrollIntoView({ block: 'center' }); } catch (e) {}
    if (hasRealImages) setLightboxOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (lightboxOpen) trackProductDetail(product);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen]);

  // На сайте открытая карточка = адрес /product/… в строке браузера, «Назад» её закрывает
  useItemUrl(lightboxOpen && hasRealImages, 'product', product, () => setLightboxOpen(false));

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = { x: t.clientX, y: t.clientY };
  };

  const lastTouchEndRef = useRef(0);

  const handleTouchEnd = (e) => {
    lastTouchEndRef.current = Date.now();
    if (!touchStartRef.current) return;
    const t = e.changedTouches[0];
    const dx = t.clientX - touchStartRef.current.x;
    const dy = t.clientY - touchStartRef.current.y;
    touchStartRef.current = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
      if (!flipPhotos) return;
      if (dx < 0) nextImage(); else prevImage();
    } else if (Math.abs(dx) < 10 && Math.abs(dy) < 10 && hasRealImages) {
      // Тап-зоны: левые/правые 25% листают фото, центральные 50% открывают карточку
      const rect = e.currentTarget.getBoundingClientRect();
      const tapX = t.clientX - rect.left;
      if (flipPhotos && tapX < rect.width * 0.25) prevImage();
      else if (flipPhotos && tapX > rect.width * 0.75) nextImage();
      else setLightboxOpen(true);
    }
  };

  const handleClick = (e) => {
    // Недавний тач уже обработан в handleTouchEnd — глушим синтезированный click.
    // Мышь/трекпад на тач-устройстве (планшет с клавиатурой) при этом работает —
    // раньше «ontouchstart in window» глушил клики мыши совсем.
    if (Date.now() - lastTouchEndRef.current < 700) return;
    if (!hasRealImages) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    if (flipPhotos && clickX < rect.width * 0.25) prevImage();
    else if (flipPhotos && clickX > rect.width * 0.75) nextImage();
    else setLightboxOpen(true);
  };

  const handleAddToCart = (e) => {
    e.stopPropagation();
    if (canAddMore) {
      addToCart(product);
      trackProductAddToCart(product, step);
    }
  };

  const handleIncrement = () => {
    if (canAddMore) {
      updateQuantity(product.id, step);
      trackProductAddToCart(product, step);
    }
  };

  const handleDecrement = () => {
    updateQuantity(product.id, -step);
  };

  const currentImage_data = images[currentImage] || images[0] || { default: '💐' };

  return (
    <div className="product-card" ref={cardRef}>
      <div
        className="product-image-container"
        ref={imageBoxRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={handleClick}
      >
        <div className="product-image-placeholder">
          {hasRealImages ? (
            <>
              {images.map((img, index) => imageReady[index] && (
                <img
                  key={index}
                  src={cardUrls[index]}
                  alt={product.name}
                  decoding="async"
                  onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                  style={{
                    position: index === 0 ? 'relative' : 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    opacity: index === currentImage ? 1 : 0,
                    transition: 'opacity 0.25s ease',
                    pointerEvents: 'none',
                  }}
                />
              ))}
            </>
          ) : (
            currentImage_data.default || currentImage_data
          )}
        </div>
        
        {/* Стрелки навигации — листают фото, не открывая просмотр */}
        {flipPhotos && (
          <>
            <span className="image-nav image-nav-prev"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
              onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); prevImage(); }}>‹</span>
            <span className="image-nav image-nav-next"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
              onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); nextImage(); }}>›</span>
          </>
        )}

        {/* Индикаторы (точки) */}
        {flipPhotos && (
          <div className="image-dots">
            {images.map((_, index) => (
              <span
                key={index}
                className={`image-dot ${index === currentImage ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setCurrentImage(index); }}
                onTouchEnd={(e) => { e.stopPropagation(); e.preventDefault(); setCurrentImage(index); }}
              />
            ))}
          </div>
        )}

        <button
          className={`add-to-cart-btn ${!canAddMore ? 'disabled' : ''}`}
          onClick={handleAddToCart}
          onTouchEnd={(e) => e.stopPropagation()}
          aria-label="Добавить в корзину"
          disabled={!canAddMore}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <path d="M16 10a4 4 0 0 1-8 0"></path>
          </svg>
        </button>
      </div>
      <div className="product-info" style={{ position: 'relative' }}>
        <h3 className="product-name">{product.name}</h3>
        <div className="stock-labels">
          {effectiveRetail >= step && (
            <span className="stock-label stock-label--current">В наличии: {effectiveRetail} шт</span>
          )}
          {nearestFuture && Math.floor((nearestFuture.available || 0) / step) * step > 0 && (
            <span className="stock-label stock-label--future">
              С {new Date(nearestFuture.date + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}: {Math.floor((nearestFuture.available || 0) / step) * step} шт
            </span>
          )}
          {usedByBouquetsTakesStock && (
            <span className="stock-label" style={{ background: '#FFF3E0', color: '#8C5A1E' }}>
              Пересекается с другими позициями в корзине
            </span>
          )}
        </div>
        <div className="product-price-row">
          <div className="product-price-info">
            <span className="product-price">
              {(quantityInCart > 0 ? priceInCart : displayPrice).toLocaleString('ru-RU')} <span>₽</span>
            </span>
            <span className="product-step-badge">за {quantityInCart > 0 ? quantityInCart : step} шт</span>
          </div>
        </div>
        {lightboxOpen && hasRealImages && (
          <PhotoLightbox
            images={images.map(img => getImageUrl(img.large || img.default))}
            previews={cardUrls}
            startIndex={currentImage}
            alt={product.name}
            onClose={() => setLightboxOpen(false)}
            onIndexChange={setCurrentImage}
            panel={
              <>
                <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>{product.name}</div>
                <div className="stock-labels" style={{ marginBottom: 10 }}>
                  {effectiveRetail >= step && (
                    <span className="stock-label stock-label--current">В наличии: {effectiveRetail} шт</span>
                  )}
                  {nearestFuture && Math.floor((nearestFuture.available || 0) / step) * step > 0 && (
                    <span className="stock-label stock-label--future">
                      С {new Date(nearestFuture.date + 'T00:00:00').toLocaleDateString('ru-RU', { day: 'numeric', month: 'long' })}: {Math.floor((nearestFuture.available || 0) / step) * step} шт
                    </span>
                  )}
                </div>
                <div style={{ marginBottom: 10 }}>
                  <span style={{ fontSize: 20, fontWeight: 800 }}>
                    {(quantityInCart > 0 ? priceInCart : displayPrice).toLocaleString('ru-RU')}₽
                  </span>
                  <span className="product-step-badge" style={{ marginLeft: 8 }}>
                    за {quantityInCart > 0 ? quantityInCart : step} шт
                  </span>
                </div>
                {/* Одна строка действий постоянной высоты: после добавления панель
                    не растёт и фото сверху не сжимается */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {quantityInCart > 0 ? (
                    <>
                      <button className="primary-btn primary-btn--cart" style={{ padding: '8px 16px' }} onClick={handleDecrement}>−</button>
                      <span style={{ fontWeight: 700, minWidth: 24, textAlign: 'center' }}>{quantityInCart}</span>
                      <button className="primary-btn primary-btn--cart" style={{ padding: '8px 16px', opacity: canAddMore ? 1 : 0.4 }}
                        onClick={handleIncrement} disabled={!canAddMore}>+</button>
                      <button
                        className="primary-btn primary-btn--cart"
                        style={{ flex: 1, padding: '12px 16px' }}
                        onClick={() => {
                          setLightboxOpen(false);
                          window.dispatchEvent(new Event('app:go-to-cart'));
                        }}
                      >
                        Перейти в корзину
                      </button>
                    </>
                  ) : (
                    <button className="primary-btn" onClick={handleAddToCart} disabled={!canAddMore}
                      style={{ width: '100%', padding: '12px 16px', opacity: canAddMore ? 1 : 0.5 }}>
                      В корзину
                    </button>
                  )}
                </div>
              </>
            }
          />
        )}
        {quantityInCart > 0 && (
          <div className="quantity-controls" style={{ position: 'absolute', bottom: 0, right: 8, flexDirection: 'column', gap: 0 }}>
            <span
              className={`quantity-btn ${!canAddMore ? 'disabled' : ''}`}
              onClick={handleIncrement}
              style={{ opacity: canAddMore ? 1 : 0.4 }}
            >
              +
            </span>
            <span className="quantity">{quantityInCart}</span>
            <span className="quantity-btn" onClick={handleDecrement}>−</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default ProductCard;
